// src/pages/PatientDashboardPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Stack,
  IconButton,
  CircularProgress,
  Badge,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  MedicalServices as MedicalIcon,
  Person as PersonIcon,
  Notifications as NotificationIcon,
  ArrowForward as ArrowForwardIcon,
  MoreVert as MoreVertIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  Insights as InsightsIcon,
  BarChart as BarChartIcon,
  PieChartOutline as PieChartIcon,
  VideoCameraFront as TeleIcon,
  Place as InPersonIcon,
  DoneAll as ReadAllIcon,
  CheckCircleOutline as ReadIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../axiosInstance';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { BarChart, PieChart } from '@mui/x-charts';

// Utils
const safeDate = (v) => { try { return v ? new Date(v) : null; } catch { return null; } };
const isFuture = (d) => !!d && d.getTime() >= Date.now();
const isPast = (d) => !!d && d.getTime() < Date.now();

// Trunca texto largo con elipsis
const ellipsize = (str, max = 22) => {
  const s = String(str || '');
  return s.length <= max ? s : s.slice(0, max - 1) + '…';
};

const formatDate = (dateString) => {
  try { return format(new Date(dateString), 'PPP', { locale: es }); }
  catch { return 'Fecha no disponible'; }
};
const formatTime = (dateString) => {
  try { return format(new Date(dateString), 'HH:mm', { locale: es }); }
  catch { return '--:--'; }
};
const formatDateTime = (dateString) => {
  try { return format(new Date(dateString), "d 'de' MMM, HH:mm", { locale: es }); }
  catch { return ''; }
};

// Tarjeta de estadística compacta
const StatCard = ({ icon, label, value, color, onClick }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: 3,
        cursor: onClick ? 'pointer' : 'default',
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
        transition: 'all 0.25s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 10px 24px ${alpha(theme.palette.common.black, 0.12)}`,
          borderColor: alpha(theme.palette.primary.main, 0.35),
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        },
      }}
    >
      <Box display="flex" alignItems="center">
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: alpha(color || theme.palette.primary.main, 0.12),
            color: color || theme.palette.primary.main,
            mr: 2,
            width: 44,
            height: 44,
          }}
        >
          {icon}
        </Avatar>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" fontWeight="bold">
            {value}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

// Contenedor para gráficos
const ChartCard = ({ title, icon, children, action }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.06)}`
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center">
          <Avatar sx={{ mr: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }}>
            {icon}
          </Avatar>
          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>
        </Box>
        {action}
      </Box>
      <Divider sx={{ mb: 2 }} />
      {children}
    </Paper>
  );
};

const PatientDashboardPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [patientData, setPatientData] = useState(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [appointmentsRaw, setAppointmentsRaw] = useState([]); // lista completa para analítica
  const [recentMedicalRecords, setRecentMedicalRecords] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notifBusy, setNotifBusy] = useState(false);

  // Utilidad: extrae lista desde array o paginado DRF
  const toList = (raw) => Array.isArray(raw) ? raw : (raw?.results ?? []);

  const loadData = async (signal) => {
    setLoading(true);
    try {
      const profileReq = axiosInstance.get('/patients/profile/', { signal });
      const apptsReq   = axiosInstance.get('/patients/appointments/', { signal });
      const recordsReq = axiosInstance.get('/patients/medical-history/', { signal });
      const notifReq   = axiosInstance.get('/patients/notifications/', { signal });

      const [profileRes, apptsRes, recordsRes, notifRes] = await Promise.allSettled([
        profileReq, apptsReq, recordsReq, notifReq
      ]);

      // Perfil
      if (profileRes.status === 'fulfilled') {
        setPatientData(profileRes.value.data);
      } else {
        setPatientData({
          first_name: 'Paciente',
          last_name: '',
          email: '',
          fecha_nacimiento: null,
          phone_number: ''
        });
        console.warn('Perfil falló:', profileRes.reason);
      }

      // Citas (guardar crudo para analítica + derivar próximas)
      if (apptsRes.status === 'fulfilled') {
        const raw = toList(apptsRes.value.data);
        setAppointmentsRaw(raw);

        const upcoming = raw
          .filter(a => {
            const start = safeDate(a.fecha_hora_cita);
            return start && isFuture(start) && a.estado_cita !== 'Cancelada';
          })
          .sort((a, b) => new Date(a.fecha_hora_cita) - new Date(b.fecha_hora_cita))
          .slice(0, 3);

        setUpcomingAppointments(upcoming);
      } else {
        setAppointmentsRaw([]);
        setUpcomingAppointments([]);
        console.warn('Citas falló:', apptsRes.reason);
      }

      // Historial (3 más recientes)
      if (recordsRes.status === 'fulfilled') {
        const raw = toList(recordsRes.value.data);
        const recent = raw
          .slice()
          .sort((a, b) => new Date(b.fecha_hora_consulta) - new Date(a.fecha_hora_consulta))
          .slice(0, 3);
        setRecentMedicalRecords(recent);
      } else {
        setRecentMedicalRecords([]);
        console.warn('Historial falló:', recordsRes.reason);
      }

      // Notificaciones
      if (notifRes.status === 'fulfilled') {
        const raw = Array.isArray(notifRes.value.data) ? notifRes.value.data : (notifRes.value.data?.results ?? []);
        setNotifications(raw);
      } else {
        const status = notifRes.reason?.response?.status;
        if (status === 404) {
          setNotifications([]); // endpoint no existe
        } else {  
        setNotifications([]);
        console.warn('Notificaciones falló:', notifRes.reason);
      }
    }  
    } catch (err) {
      // Cancelaciones: Axios / AbortController
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || err?.name === 'AbortError') {
        return;
      }
      console.error('Error cargando dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  // Acciones de notificaciones
  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

  const markAllNotificationsRead = async () => {
    try {
      setNotifBusy(true);
      await axiosInstance.patch('/patients/notifications/read_all/');
      setNotifications((prev) => prev.map(n => ({ ...n, read: true })));
    } catch (e) {
      console.error('Error marcando todas como leídas:', e);
    } finally {
      setNotifBusy(false);
    }
  };

  const markNotificationRead = async (id) => {
    try {
      setNotifBusy(true);
      await axiosInstance.patch(`/patients/notifications/${id}/read/`);
      setNotifications((prev) => prev.map(n => n.id === id ? ({ ...n, read: true }) : n));
    } catch (e) {
      console.error('Error marcando notificación como leída:', e);
    } finally {
      setNotifBusy(false);
    }
  };

  const openNotificationLink = (n) => {
    const link = (n?.link || '').trim();
    if (!link) return;
    // Si es ruta interna, navegar; si es URL absoluta, abrir
    if (link.startsWith('/')) navigate(link);
    else if (link.startsWith('http')) window.open(link, '_blank', 'noopener,noreferrer');
  };

  // Cargar al entrar y cuando cambia la key de navegación (re-entradas)
  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Derivados para tarjetas/estadísticas y gráficos
  const stats = useMemo(() => {
    const nextAppt = upcomingAppointments[0];
    const nextApptLabel = nextAppt
      ? `${formatDate(nextAppt.fecha_hora_cita)} • ${formatTime(nextAppt.fecha_hora_cita)}`
      : '—';

    const totalUpcoming = upcomingAppointments.length;
    const totalRecentRecords = recentMedicalRecords.length;

    return { nextApptLabel, totalUpcoming, totalRecentRecords };
  }, [upcomingAppointments, recentMedicalRecords]);

  // Gráfico: Top doctores visitados (por citas pasadas)
  const doctorsChart = useMemo(() => {
    const normalize = (s) =>
      String(s || '')
        .normalize('NFD').replace(/\p{Diacritic}/gu, '')
        .toLowerCase();

    const CANCELLED = new Set(['cancelada', 'cancelado']);

    const countTop = (arr) => {
      const counts = new Map();
      arr.forEach(a => {
        const name = a.medico_nombre || 'Dr. (desconocido)';
        counts.set(name, (counts.get(name) || 0) + 1);
      });
      const entries = Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6);
      return {
        xLabels: entries.map(([name]) => ellipsize(name, 18)),
        series: [{ data: entries.map(([, v]) => v), label: 'Visitas' }],
        empty: entries.length === 0,
      };
    };

    const past = appointmentsRaw.filter(a => {
      const d = safeDate(a.fecha_hora_cita);
      const estado = normalize(a?.estado_cita);
      return d && isPast(d) && !CANCELLED.has(estado);
    });

    let chart = countTop(past);

    if (chart.empty) {
      const future = appointmentsRaw.filter(a => {
        const d = safeDate(a.fecha_hora_cita);
        const estado = normalize(a?.estado_cita);
        return d && isFuture(d) && !CANCELLED.has(estado);
      });
      chart = countTop(future);
      chart.series[0].label = 'Agendadas';
    }

    return chart;
  }, [appointmentsRaw]);

  // Gráfico: Modalidad Presencial vs Telemedicina (en todas las citas)
  const modalityChart = useMemo(() => {
    let presencial = 0, tele = 0;
    appointmentsRaw.forEach(a => {
      const mod = (a.modalidad || '').toLowerCase();
      if (mod === 'telemedicina') tele += 1;
      else if (mod === 'presencial') presencial += 1;
    });
    const total = presencial + tele;
    return {
      data: [
        { id: 0, label: 'Presencial', value: presencial },
        { id: 1, label: 'Telemedicina', value: tele },
      ],
      empty: total === 0
    };
  }, [appointmentsRaw]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={64} thickness={4} />
        <Typography variant="body2" color="text.secondary">Cargando tu panel...</Typography>
      </Box>
    );
  }

  const fullName = patientData
    ? `${patientData.first_name || ''} ${patientData.last_name || ''}`.trim() || 'Paciente'
    : 'Paciente';
  const email = patientData?.email || '';
  const phone = patientData?.phone_number || '';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Encabezado con saludo y acciones rápidas */}
      <Box sx={{ mb: 4 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          rowGap={2}
        >
          {/* Contenedor con icono 56x56 + textos */}
          <Box display="flex" alignItems="center" gap={2.0}>
            <Avatar
              variant="rounded"
              sx={{
                width: 56,
                height: 56,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
                borderRadius: 2.5,
              }}
              aria-label="Identidad del portal"
            >
              <MedicalIcon fontSize="large" />
            </Avatar>

            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ letterSpacing: 0.2 }}>
                Hola, {patientData?.first_name || 'Paciente'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Bienvenido/a a tu portal de salud. Gestiona tus citas y consulta tu historial médico.
              </Typography>
            </Box>
          </Box>

          {/* Acciones rápidas */}
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CalendarIcon />}
              onClick={() => navigate('/patient/doctors')}
              sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 'bold' }}
            >
              Agendar cita
            </Button>
            <Button
              variant="outlined"
              startIcon={<PersonIcon />}
              onClick={() => navigate('/patient/profile')}
              sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 'bold' }}
            >
              Editar perfil
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Columna izquierda */}
        <Grid item xs={12} md={4}>
          {/* Perfil */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 4,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              boxShadow: `0 6px 24px ${alpha(theme.palette.common.black, 0.06)}`
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <Tooltip title="Tu perfil">
                    <Avatar sx={{ width: 28, height: 28, bgcolor: theme.palette.secondary.main, border: '2px solid white' }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                  </Tooltip>
                }
              >
                <Avatar
                  sx={{
                    width: 100, height: 100, mb: 2,
                    bgcolor: theme.palette.primary.main,
                    fontSize: '2.5rem',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                  }}
                >
                  {(patientData?.first_name || 'P').charAt(0).toUpperCase()}
                </Avatar>
              </Badge>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {fullName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {(() => {
                  if (!patientData?.fecha_nacimiento) return '';
                  const today = new Date();
                  const birth = new Date(patientData.fecha_nacimiento);
                  let age = today.getFullYear() - birth.getFullYear();
                  const mdiff = today.getMonth() - birth.getMonth();
                  if (mdiff < 0 || (mdiff === 0 && today.getDate() < birth.getDate())) age--;
                  return `${age} años`;
                })()}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemText
                  primary="Correo electrónico"
                  secondary={email || 'No registrado'}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Teléfono"
                  secondary={phone || 'No registrado'}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1' }}
                />
              </ListItem>
            </List>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<PersonIcon />}
              onClick={() => navigate('/patient/profile')}
              sx={{ mt: 2, borderRadius: 8, textTransform: 'none', fontWeight: 'medium' }}
            >
              Editar perfil
            </Button>
          </Paper>

          {/* Notificaciones */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              boxShadow: `0 6px 24px ${alpha(theme.palette.common.black, 0.06)}`
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }}>
                  <NotificationIcon />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Notificaciones
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  label={unreadCount}
                  color="primary"
                  size="small"
                  sx={{ borderRadius: 4 }}
                />
                <Tooltip title="Marcar todas como leídas">
                  <span>
                    <IconButton
                      size="small"
                      onClick={markAllNotificationsRead}
                      disabled={notifBusy || unreadCount === 0}
                    >
                      <ReadAllIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {notifications.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No tienes notificaciones nuevas
              </Typography>
            ) : (
              <List>
                {notifications.map((n) => (
                  <ListItem key={n.id} disablePadding sx={{ mb: 1 }}>
                    {/* Tarjeta de notificación con acciones abajo */}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        px: 1.5,
                        width: '100%',
                        borderRadius: 2,
                        bgcolor: n.read ? 'transparent' : alpha(theme.palette.primary.light, 0.12),
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <ListItemAvatar sx={{ minWidth: 0 }}>
                          <Avatar sx={{ bgcolor: n.read ? theme.palette.grey[400] : theme.palette.primary.main }}>
                            <NotificationIcon />
                          </Avatar>
                        </ListItemAvatar>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Título + link */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body1" fontWeight={n.read ? 'regular' : 'bold'} noWrap>
                              {n.title}
                            </Typography>
                            {n.link && (
                              <Tooltip title="Abrir">
                                <IconButton size="small" onClick={() => openNotificationLink(n)}>
                                  <LinkIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>

                          {/* Mensaje */}
                          {n.message && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                              {n.message}
                            </Typography>
                          )}

                          {/* Fila inferior: fecha izquierda, acciones derecha */}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mt: 0.75,
                              flexWrap: 'wrap',
                              rowGap: 0.5,
                            }}
                          >
                            <Typography variant="caption" color="text.disabled">
                              {formatDateTime(n.created_at)}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title={n.read ? 'Leída' : 'Marcar como leída'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => markNotificationRead(n.id)}
                                    disabled={notifBusy || n.read}
                                  >
                                    <ReadIcon color={n.read ? 'success' : 'disabled'} fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <IconButton
                                size="small"
                                onClick={() => navigate('/patient/appointments')}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Columna derecha */}
        <Grid item xs={12} md={8}>
          {/* Tarjetas de estadísticas rápidas */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={<CalendarIcon />}
                label="Próxima cita"
                value={stats.nextApptLabel}
                color={theme.palette.primary.main}
                onClick={() => navigate('/patient/appointments')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={<EventIcon />}
                label="Citas programadas"
                value={String(stats.totalUpcoming)}
                color={theme.palette.success.main}
                onClick={() => navigate('/patient/appointments')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={<MedicalIcon />}
                label="Registros recientes"
                value={String(stats.totalRecentRecords)}
                color={theme.palette.secondary.main}
                onClick={() => navigate('/patient/medical-records')}
              />
            </Grid>
          </Grid>

          {/* Próximas citas */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 4,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.06)}`
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, mr: 2 }}>
                  <CalendarIcon />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Próximas Citas
                </Typography>
              </Box>
              <Button
                variant="text"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/patient/appointments')}
                sx={{ textTransform: 'none' }}
              >
                Ver todas
              </Button>
            </Box>

            {upcomingAppointments.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No tienes citas programadas
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/patient/doctors')}
                  sx={{ mt: 2, borderRadius: 8, textTransform: 'none', fontWeight: 'medium', px: 3 }}
                >
                  Agendar una cita
                </Button>
              </Box>
            ) : (
              <Box>
                {upcomingAppointments.map((appointment) => (
                  <Card
                    key={appointment.id_cita}
                    variant="outlined"
                    sx={{
                      mb: 2,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                      boxShadow: `0 2px 10px ${alpha(theme.palette.common.black, 0.06)}`,
                      '&:hover': { boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.12)}` },
                      transition: 'box-shadow 0.25s ease'
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={2} sm={1}>
                          <Avatar
                            sx={{
                              bgcolor: theme.palette.primary.main,
                              width: { xs: 40, sm: 50 },
                              height: { xs: 40, sm: 50 }
                            }}
                          >
                            {appointment.medico_nombre?.charAt(0) || 'D'}
                          </Avatar>
                        </Grid>
                        <Grid item xs={10} sm={7}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {appointment.medico_nombre}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {appointment.medico_especialidad}
                          </Typography>
                          {appointment.motivo_cita && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {appointment.motivo_cita}
                            </Typography>
                          )}
                          {appointment.modalidad && (
                            <Chip
                              size="small"
                              label={appointment.modalidad}
                              icon={appointment.modalidad === 'Telemedicina' ? <TeleIcon /> : <InPersonIcon />}
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Grid>
                        <Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                            <EventIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                            <Typography variant="body2" fontWeight="medium">
                              {formatDate(appointment.fecha_hora_cita)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                              mt: 0.5
                            }}
                          >
                            <TimeIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                            <Typography variant="body2" fontWeight="medium">
                              {formatTime(appointment.fecha_hora_cita)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}

                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/patient/doctors')}
                    sx={{ borderRadius: 8, textTransform: 'none', fontWeight: 'medium', px: 3 }}
                  >
                    Agendar nueva cita
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Historial médico reciente */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.06)}`
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.12), color: theme.palette.secondary.main, mr: 2 }}>
                  <MedicalIcon />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Historial Médico Reciente
                </Typography>
              </Box>
              <Button
                variant="text"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/patient/medical-records')}
                sx={{ textTransform: 'none' }}
              >
                Ver todo
              </Button>
            </Box>

            {recentMedicalRecords.length === 0 ? (
              <Typography variant="body1" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No hay registros médicos disponibles
              </Typography>
            ) : (
              <List>
                {recentMedicalRecords.map((record) => (
                  <ListItem
                    key={record.id_consulta}
                    sx={{
                      bgcolor: alpha(theme.palette.background.default, 0.7),
                      borderRadius: 2,
                      mb: 2
                    }}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => navigate(`/patient/medical-records/${record.id_consulta}`)}>
                        <MoreVertIcon />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                        <MedicalIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={record.diagnostico_principal}
                      secondary={`${record.medico_nombre} • ${formatDate(record.fecha_hora_consulta)}`}
                      primaryTypographyProps={{ fontWeight: 'medium' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Sección de analítica y gráficos */}
      <Grid container spacing={4} sx={{ mt: 1 }}>
        <Grid item xs={12} md={7}>
          <ChartCard
            title="Doctores visitados (recientes)"
            icon={<BarChartIcon />}
            action={
              <Button size="small" variant="outlined" onClick={() => navigate('/patient/appointments')} sx={{ textTransform: 'none', borderRadius: 2 }}>
                Ver citas
              </Button>
            }
          >
            {doctorsChart.empty ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                Aún no hay visitas registradas.
              </Typography>
            ) : (
              <Box sx={{ width: '100%' }}>
                <BarChart
                  height={260}
                  xAxis={[{ scaleType: 'band', data: doctorsChart.xLabels }]}
                  series={doctorsChart.series}
                  colors={[theme.customColors?.primary?.dark || theme.palette.primary.light]}
                  sx={{
                    '.MuiChartsLegend-root': { display: 'none' },
                  }}
                />
              </Box>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <ChartCard title="Modalidad de citas" icon={<PieChartIcon />}>
            {modalityChart.empty ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                Sin datos de citas para comparar modalidad.
              </Typography>
            ) : (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <PieChart
                  height={260}
                  series={[
                    {
                      data: modalityChart.data,
                      innerRadius: 40,
                      paddingAngle: 4,
                      cornerRadius: 4,
                    }
                  ]}
                  colors={[theme.palette.info.main, theme.palette.success.main]}
                  slotProps={{
                    legend: { hidden: false, direction: 'row' },
                  }}
                />
              </Box>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Container>
  );

};

export default PatientDashboardPage;
